import React, {useEffect, useRef, useState} from 'react';

/**
 * Каскадное появление группы элементов при скролле — **только для витринных
 * поверхностей** (лендинг). Функциональный UI, который открывают каждый день,
 * так анимировать нельзя: интерфейс начинает спорить со своим читателем.
 *
 * Сознательно применяется точечно, а не к каждой секции: одинаковый выезд у
 * всего подряд — это не композиция, а тик. На лендинге каскад достаётся одной
 * бенто-сетке, у hero свой авторский момент (см. AppMockup).
 *
 * Наблюдатель висит на самом контейнере и переключает `data-visible`; задержки
 * раздаёт CSS по `nth-child` (см. `.fw-reveal-group` в index.css). Так у
 * элементов сетки не появляется лишних обёрток, которые ломали бы `col-span`.
 * Срабатывает один раз.
 */
interface RevealGroupProps {
    children: React.ReactNode;
    className?: string;
}

/**
 * Без `IntersectionObserver` (jsdom в тестах, отключённый JS, старый браузер)
 * содержимое видно сразу: контент не должен зависеть от анимации.
 */
const supportsObserver = (): boolean =>
    typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';

const RevealGroup: React.FC<RevealGroupProps> = ({children, className = ''}) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(() => !supportsObserver());

    useEffect(() => {
        if (visible || !ref.current) return;

        const observer = new IntersectionObserver(
            entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            // Группа считается «пришедшей», когда её верх поднялся на 80px выше
            // нижней границы экрана: каскад успевает отыграть до того, как
            // читатель доберётся до плиток.
            {rootMargin: '0px 0px -80px 0px'},
        );

        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [visible]);

    return (
        <div ref={ref} data-visible={visible || undefined} className={`fw-reveal-group ${className}`}>
            {children}
        </div>
    );
};

export default RevealGroup;
